import { useState, useRef } from 'react';
import axios from 'axios';
import { fbqTrack } from '../lib/fbpixel';
import { Button } from './ui/button';
import { Loader2, Upload, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared component for pending/rejected users to upload deposit screenshot.
 * Auto-moves rejected → pending on success.
 *
 * Props:
 *  - currentUrl: existing screenshot path (string)
 *  - onSuccess: callback({path, url, status})
 *  - ctaLabel: button text
 */
export function ScreenshotUploader({ currentUrl, onSuccess, ctaLabel = 'Upload Deposit Screenshot' }) {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentUrl ? `${API}/files/${currentUrl}` : null);
    const inputRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error('File too large (max 8 MB)');
            return;
        }

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await axios.post(`${API}/auth/upload-screenshot`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setPreview(URL.createObjectURL(file));
            toast.success('Screenshot uploaded — your account is now pending review');
            fbqTrack('Lead', { content_name: 'Deposit Screenshot Upload' });
            if (onSuccess) onSuccess(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <div data-testid="screenshot-uploader">
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="screenshot-file-input" />

            {preview && (
                <div className="relative mb-3 rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
                    <img src={preview} alt="Deposit proof" className="w-full max-h-48 object-contain" data-testid="screenshot-preview" />
                    <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1" style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-text)' }}>
                        <CheckCircle2 className="w-3 h-3" />
                        Uploaded
                    </div>
                </div>
            )}

            <Button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="btn-primary w-full"
                data-testid="screenshot-upload-btn"
            >
                {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</>
                ) : preview ? (
                    <><Upload className="w-4 h-4 mr-2" />Replace Screenshot</>
                ) : (
                    <><ImageIcon className="w-4 h-4 mr-2" />{ctaLabel}</>
                )}
            </Button>
        </div>
    );
}
